"use client";

import { Dialog as SheetPrimitive } from "@base-ui/react/dialog";
import { IconX } from "@teral/icons";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";

import { cn } from "cn";

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Backdrop>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Backdrop>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Backdrop className={cn(className)} {...props} ref={ref} />
));
SheetOverlay.displayName = SheetPrimitive.Backdrop.displayName;

const sheetVariants = cva(
  "fixed z-50 gap-4 bg-raised p-6 shadow-floating transition ease-in-out data-closed:duration-300 data-open:duration-500",
  {
    variants: {
      overlay: {
        transparent: "bg-transparent shadow-none",
        default:
          "fixed inset-0 z-50 bg-background/80 backdrop-blur-xs transition-opacity data-starting-style:opacity-0 data-ending-style:opacity-0",
      },
      side: {
        top: "inset-x-0 top-0 border-b data-starting-style:-translate-y-full data-ending-style:-translate-y-full",
        bottom:
          "inset-x-0 bottom-0 border-t data-starting-style:translate-y-full data-ending-style:translate-y-full",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-starting-style:-translate-x-full data-ending-style:-translate-x-full sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4  border-l data-starting-style:translate-x-full data-ending-style:translate-x-full sm:max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
);

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Popup>,
    VariantProps<typeof sheetVariants> {
  closeIcon?: React.ReactNode | undefined;
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Popup>,
  SheetContentProps & { disableClose?: boolean }
>(
  (
    {
      closeIcon,
      side = "right",
      className,
      children,
      overlay = "default",
      disableClose = false,
      ...props
    },
    ref,
  ) => {
    const variants = React.useMemo(() => sheetVariants({ overlay, side }), [overlay, side]);

    return (
      <SheetPortal>
        <SheetOverlay className={cn(variants)} />
        <SheetPrimitive.Popup ref={ref} className={cn(variants, className)} {...props}>
          {children}
          {!disableClose && (
            <SheetPrimitive.Close className="absolute right-4 top-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-accentA-5 focus:ring-offset-2 disabled:pointer-events-none data-open:bg-gray-3">
              {closeIcon ? closeIcon : <IconX className="h-4 w-4 text-grayA-11" />}
              <span className="sr-only">Close</span>
            </SheetPrimitive.Close>
          )}
        </SheetPrimitive.Popup>
      </SheetPortal>
    );
  },
);
SheetContent.displayName = SheetPrimitive.Popup.displayName;

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col gap-2 text-center sm:text-left", className)} {...props} />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:gap-2", className)}
    {...props}
  />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-gray-12", className)}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-gray-11", className)}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
